export enum PersonalizeToneOption {
  warmRelatable = "warm_relatable",
  enthusiasticUpbeat = "enthusiastic_upbeat",
  playfulWitty = "playful_witty",
  professionalFormal = "professional_formal",
  confidentBold = "confident_bold",
  briefDirect = "brief_direct",
  makeShorter = "make_shorter",
  addDetail = "add_detail",
}

export const TONE_OPTIONS: {
  id: PersonalizeToneOption;
  label: string;
  emoji: string;
}[] = [
  {
    id: PersonalizeToneOption.makeShorter,
    label: "Make it shorter",
    emoji: "➖",
  },
  {
    id: PersonalizeToneOption.addDetail,
    label: "Add more detail",
    emoji: "➕",
  },
  {
    id: PersonalizeToneOption.warmRelatable,
    label: "Warm & Relatable",
    emoji: "🏡",
  },
  {
    id: PersonalizeToneOption.enthusiasticUpbeat,
    label: "Enthusiastic & Upbeat",
    emoji: "⚡",
  },
  {
    id: PersonalizeToneOption.playfulWitty,
    label: "Playful & Witty",
    emoji: "😄",
  },
  {
    id: PersonalizeToneOption.professionalFormal,
    label: "Professional & Formal",
    emoji: "👔",
  },
  {
    id: PersonalizeToneOption.confidentBold,
    label: "Confident & Bold",
    emoji: "🔥",
  },
  {
    id: PersonalizeToneOption.briefDirect,
    label: "Brief & Direct",
    emoji: "✂️",
  },
];

export type ValidationKey = "product" | "content_idea" | "case_study" | null;

export function validateForm(state: FormState): ValidationKey {
  if (state.referenceProduct && state.productRef.kind === "user_input") {
    if (!(state.productRef.userInput ?? "").trim()) return "product";
  }
  if (state.contentIdea && state.contentIdeaState.kind === "user_text") {
    if (!(state.contentIdeaState.text ?? "").trim()) return "content_idea";
  }
  if (state.mentionCaseStudy && !(state.caseStudyText ?? "").trim()) {
    return "case_study";
  }
  return null;
}

export type ProductRefKind = "existing" | "ai_find" | "user_input";

export type ProductRefState = {
  kind: ProductRefKind;
  productId?: number;
  userInput?: string;
};

export type ContentIdeaKind = "ai_generate" | "user_text";

export type ContentIdeaState = {
  kind: ContentIdeaKind;
  text?: string;
};

export type FormState = {
  referenceInstagram: boolean;
  referenceProduct: boolean;
  productRef: ProductRefState;
  mentionCaseStudy: boolean;
  caseStudyText: string;
  contentIdea: boolean;
  contentIdeaState: ContentIdeaState;
  audienceAlignment: boolean;
  tone: PersonalizeToneOption | null;
  freeform: string;
};

export const initialFormState: FormState = {
  referenceInstagram: false,
  referenceProduct: false,
  productRef: { kind: "ai_find" },
  mentionCaseStudy: false,
  caseStudyText: "",
  contentIdea: false,
  contentIdeaState: { kind: "ai_generate" },
  audienceAlignment: false,
  tone: null,
  freeform: "",
};

export type PersonalizeProductRef =
  | { kind: "existing"; productId: number }
  | { kind: "ai_find" }
  | { kind: "user_input"; value: string };

export type PersonalizeSource = "personalize_dialog" | "chat_helper";

export type PersonalizeBaseSource = "editor" | "previous_ai";

export type PersonalizeRequestPayload = {
  bentoBrandId: number;
  contactName?: string;
  currentDraftHtml: string;
  options: {
    referenceInstagram?: boolean;
    referenceProduct?: PersonalizeProductRef | false;
    mentionCaseStudy?: { text: string } | false;
    contentIdea?:
      | { kind: "ai_generate" }
      | { kind: "user_text"; text: string }
      | false;
    audienceAlignment?: boolean;
  };
  tone?: PersonalizeToneOption | null;
  freeform?: string;
  source?: PersonalizeSource;
  baseSource?: PersonalizeBaseSource;
};

export type StepKey =
  | "instagram_refresh"
  | "product_search"
  | "product_fetch"
  | "content_idea"
  | "case_study"
  | "audience"
  | "draft";

export type StepStatus = "pending" | "active" | "done" | "error";

export type StepState = {
  key: StepKey;
  label: string;
  status: StepStatus;
  errorMessage?: string;
};

export const STEP_LABELS: Record<StepKey, string> = {
  instagram_refresh: "Fetching brand's Instagram data",
  product_search: "Searching the web for products",
  product_fetch: "Fetching product details",
  content_idea: "Generating a content idea",
  case_study: "Analyzing your case study",
  audience: "Analyzing your audience data",
  draft: "Personalizing your draft",
};

export function stepsForForm(state: FormState): StepKey[] {
  const out: StepKey[] = [];
  if (state.referenceInstagram) {
    out.push("instagram_refresh");
  }
  if (state.referenceProduct) {
    if (state.productRef.kind === "ai_find") {
      out.push("product_search");
    } else if (state.productRef.kind === "user_input") {
      const v = state.productRef.userInput ?? "";
      if (/^https?:\/\//i.test(v)) out.push("product_fetch");
    }
  }
  if (state.mentionCaseStudy) out.push("case_study");
  if (state.contentIdea && state.contentIdeaState.kind === "ai_generate") {
    out.push("content_idea");
  }
  if (state.audienceAlignment) out.push("audience");
  out.push("draft");
  return out;
}

export function buildPayload(
  state: FormState,
  bentoBrandId: number,
  currentDraftHtml: string,
  contactName?: string,
  source?: PersonalizeSource,
  baseSource?: PersonalizeBaseSource,
): PersonalizeRequestPayload {
  let referenceProduct: PersonalizeProductRef | false = false;
  if (state.referenceProduct) {
    if (state.productRef.kind === "existing" && state.productRef.productId) {
      referenceProduct = {
        kind: "existing",
        productId: state.productRef.productId,
      };
    } else if (state.productRef.kind === "ai_find") {
      referenceProduct = { kind: "ai_find" };
    } else if (state.productRef.kind === "user_input") {
      referenceProduct = {
        kind: "user_input",
        value: state.productRef.userInput ?? "",
      };
    }
  }

  let contentIdea: PersonalizeRequestPayload["options"]["contentIdea"] = false;
  if (state.contentIdea) {
    if (state.contentIdeaState.kind === "ai_generate") {
      contentIdea = { kind: "ai_generate" };
    } else {
      contentIdea = {
        kind: "user_text",
        text: state.contentIdeaState.text ?? "",
      };
    }
  }

  return {
    bentoBrandId,
    contactName,
    currentDraftHtml,
    options: {
      referenceInstagram: state.referenceInstagram,
      referenceProduct,
      mentionCaseStudy: state.mentionCaseStudy
        ? { text: state.caseStudyText }
        : false,
      contentIdea,
      audienceAlignment: state.audienceAlignment,
    },
    tone: state.tone,
    freeform: state.freeform,
    source,
    baseSource,
  };
}
